/**
 * Barcode Scanner Modal
 * =====================
 * Camera-based barcode scanning using @zxing/library.
 * Supports EAN-13, EAN-8, UPC-A barcodes.
 * Includes AI-powered fallback for difficult-to-scan barcodes.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { X, Camera, Check, RefreshCw, AlertCircle, Settings, ExternalLink, Sparkles, Loader2 } from 'lucide-react'
import { BrowserMultiFormatReader, BarcodeFormat, DecodeHintType, NotFoundException } from '@zxing/library'
import { barcodeApi } from '@/services/api'

interface BarcodeScannerModalProps {
  onScan: (barcode: string) => void
  onClose: () => void
}

// Base64-encoded short beep sound
const BEEP_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVoGAACAf4B/gH+Af4B/gH+Af4B/gH+Af4B/gH+Af4B/gH+Af4B/gH+Af4B/gH+Af4B/gH9uf2B/cH+Af5B/oH+wf8B/0H/gf/B/AICQgKCAsIDAgNCA4IDwgACBEIEggTCBQIFQgWCBcIGAgZCBoIGwgcCB0IHggfCAAIIQgiCCMIJAglCCYIJwgoCCkIKggrCCwILQguCC8IIAgxCDIIMwg0CDUINgg3CDgIOQg6CDsIPAg9CD4IPwgwCEEIQghDCEQIRQhGCEcISAhJCEoISwgH98f3B/YH9Qf0B/MH8gfxB/AH/wfuB+0H7AfrB+oH6QfoB+cH5gflB+QH4wfiB+EH4AfvB94H3QfcB9sH2gfZB9gH1wfWB9UH1AfTB9IH0QfQB88HzgfNB8wHywfKB8kHyAfHB8YHxQfEB8MHwge/B74HvAe6B7gHtgdqB14HUgdGB7sHrwejB5cHiwt/C3MLZwtbC08LQws3CysLHwsTCwcL+wrwCuQK2ArMCsAKtAqoCpwKkAqECngKbApgClQKSAo8CjAKJAoYCgwKAAr0CegJ3AnQCcQJuAmsCaAJlAmICXwJcAlkCVgJTAlACTQJKAkcCRAJBAn4CO0I4gjXCMwIwQi2CKsIoAiVCIoIfwhzCGcIWwhPCEMINwgrCB8IEwgHCPsH7wfjB9cHywi/CLMIpwiXB4sHfwdzB2cHWwdPB0MHNwcrBx8HEwcHB/sG7wbjBtcGywi/BrMGpwabBo8GgwZ3BmsGXwZTBkcGOwYvBiMGFwYLBv8F8wXnBdsLzwvDC7cLqwufC5MLhwt7C28LXwtTC0cLOws4BQQF+ATsBOAE1ATIBL0EsQSlBJkEjQSBBHUEaQRdBFEERQQ5BC0EKQR2BGoEXgRSBEYEOgQuBCIEFgQKBP4D8gPmA9oDzgPCA7YDqgOeA5IDBgT6A+4D4gPWA8oDvgOyA6YDmgOOA4IDdgNqA14DUgNGAzoDbgNiA1YDSgM+AzIDJgMaAw4DAgP2AuoC3gLSAsYCugKuAqIClgKKAn4CcgJmAloCTgJCAjYCKgIeAhICBgL6Ae4B4gHWAcoB'

type ScannerState = 'initializing' | 'scanning' | 'detected' | 'error' | 'analyzing'
type ErrorType = 'no_camera' | 'permission_denied' | 'unknown'

// Time in ms before showing the AI capture button
const AI_BUTTON_DELAY = 5000

export default function BarcodeScannerModal({ onScan, onClose }: BarcodeScannerModalProps) {
  const [state, setState] = useState<ScannerState>('initializing')
  const [errorType, setErrorType] = useState<ErrorType | null>(null)
  const [detectedBarcode, setDetectedBarcode] = useState<string | null>(null)
  const [showFlash, setShowFlash] = useState(false)
  const [scanKey, setScanKey] = useState(0) // Key to force re-initialization
  const [showAIButton, setShowAIButton] = useState(false)
  const [aiConfidence, setAiConfidence] = useState<string | null>(null)
  const [aiError, setAiError] = useState<string | null>(null)

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const readerRef = useRef<BrowserMultiFormatReader | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanLoopRef = useRef<boolean>(false)
  const aiButtonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Play beep sound
  const playBeep = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(BEEP_SOUND)
    }
    audioRef.current.currentTime = 0
    audioRef.current.play().catch(() => {
      // Ignore audio play errors (user might not have interacted with page)
    })
  }, [])

  // Show green flash effect
  const showFlashEffect = useCallback(() => {
    setShowFlash(true)
    setTimeout(() => setShowFlash(false), 300)
  }, [])

  // Stop scanner and release camera
  const stopScanner = useCallback(() => {
    scanLoopRef.current = false
    if (aiButtonTimerRef.current) {
      clearTimeout(aiButtonTimerRef.current)
      aiButtonTimerRef.current = null
    }
    if (readerRef.current) {
      readerRef.current.reset()
      readerRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
  }, [])

  // Capture frame from video and send to AI for analysis
  const handleAICapture = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return

    setState('analyzing')
    setAiError(null)
    setAiConfidence(null)

    try {
      const video = videoRef.current
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Could not get canvas context')

      // Set canvas to video dimensions
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      // Draw current video frame to canvas
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      // Convert to base64 JPEG
      const imageBase64 = canvas.toDataURL('image/jpeg', 0.9)

      console.log('[BarcodeScanner] Sending image to AI for analysis...')

      // Send to backend for Claude analysis
      const result = await barcodeApi.analyzeImage(imageBase64)

      console.log('[BarcodeScanner] AI result:', result)

      if (result.error) {
        setAiError(result.error)
        setState('scanning')
        return
      }

      if (result.barcode) {
        playBeep()
        showFlashEffect()
        setDetectedBarcode(result.barcode)
        setAiConfidence(result.confidence)
        setState('detected')
        stopScanner()
      } else {
        setAiError('No barcode found in image. Try capturing again with better lighting or angle.')
        setState('scanning')
      }
    } catch (err) {
      console.error('[BarcodeScanner] AI analysis error:', err)
      setAiError('Failed to analyze image. Please try again.')
      setState('scanning')
    }
  }, [playBeep, showFlashEffect, stopScanner])

  // Initialize scanner
  useEffect(() => {
    const initScanner = async () => {
      console.log('[BarcodeScanner] Starting initialization...')
      setShowAIButton(false)

      try {
        let stream: MediaStream | null = null

        try {
          console.log('[BarcodeScanner] Requesting camera access with HD constraints...')
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 1920, min: 1280 },
              height: { ideal: 1080, min: 720 },
              facingMode: { ideal: 'environment' }
            }
          })
          console.log('[BarcodeScanner] Camera access granted!')
        } catch (permError) {
          console.error('[BarcodeScanner] Camera access error:', permError)

          if (permError instanceof Error) {
            const errorName = (permError as DOMException).name || permError.name
            console.log('[BarcodeScanner] Error name:', errorName)

            if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
              setState('error')
              setErrorType('permission_denied')
              return
            } else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError' || errorName === 'NotReadableError') {
              setState('error')
              setErrorType('no_camera')
              return
            }
          }
          setState('error')
          setErrorType('unknown')
          return
        }

        // Create reader with hints for barcode formats
        const hints = new Map()
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E,
          BarcodeFormat.CODE_128,
          BarcodeFormat.CODE_39,
        ])
        hints.set(DecodeHintType.TRY_HARDER, true)

        const reader = new BrowserMultiFormatReader(hints)
        readerRef.current = reader

        // Enumerate devices to find best camera
        const videoInputDevices = await reader.listVideoInputDevices()
        console.log('[BarcodeScanner] Found video devices:', videoInputDevices.map(d => ({ id: d.deviceId, label: d.label })))

        if (videoInputDevices.length === 0) {
          console.error('[BarcodeScanner] No video input devices found')
          stream.getTracks().forEach(track => track.stop())
          setState('error')
          setErrorType('no_camera')
          return
        }

        // Check for rear camera on mobile
        const currentTrack = stream.getVideoTracks()[0]
        const currentDeviceId = currentTrack?.getSettings()?.deviceId

        const rearCamera = videoInputDevices.find(device =>
          device.label.toLowerCase().includes('back') ||
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        )

        if (rearCamera && rearCamera.deviceId !== currentDeviceId) {
          console.log('[BarcodeScanner] Switching to rear camera:', rearCamera.label)
          stream.getTracks().forEach(track => track.stop())
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                deviceId: { exact: rearCamera.deviceId },
                width: { ideal: 1920, min: 1280 },
                height: { ideal: 1080, min: 720 },
              }
            })
          } catch {
            console.log('[BarcodeScanner] Could not switch to rear camera, using default')
            stream = await navigator.mediaDevices.getUserMedia({ video: true })
          }
        }

        // Attach stream to video element
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          streamRef.current = stream

          // Wait for video to be ready
          await new Promise<void>((resolve, reject) => {
            if (videoRef.current) {
              videoRef.current.onloadedmetadata = () => {
                console.log('[BarcodeScanner] Video metadata loaded')
                resolve()
              }
              videoRef.current.onerror = () => {
                reject(new Error('Video element error'))
              }
            }
          })

          // Play video
          try {
            await videoRef.current.play()
            console.log('[BarcodeScanner] Video playing')
          } catch (playError) {
            console.warn('[BarcodeScanner] Video play warning:', playError)
            // Continue anyway - video might already be playing
          }
        }

        setState('scanning')
        scanLoopRef.current = true

        // Log video resolution
        const videoTrack = stream.getVideoTracks()[0]
        const settings = videoTrack?.getSettings()
        console.log('[BarcodeScanner] Video resolution:', settings?.width, 'x', settings?.height)

        // Start timer for AI button
        aiButtonTimerRef.current = setTimeout(() => {
          console.log('[BarcodeScanner] Showing AI capture button')
          setShowAIButton(true)
        }, AI_BUTTON_DELAY)

        // Start decode loop - use decodeFromImageElement with canvas snapshots
        // This avoids the "video already playing" warning from decodeFromVideoElement
        const decodeLoop = async () => {
          let scanCount = 0
          while (scanLoopRef.current && videoRef.current && streamRef.current?.active) {
            try {
              // Only decode if video is ready
              if (canvasRef.current && videoRef.current.readyState >= 2) {
                const canvas = canvasRef.current
                const ctx = canvas.getContext('2d')
                if (ctx) {
                  // Match canvas to video size
                  if (canvas.width !== videoRef.current.videoWidth) {
                    canvas.width = videoRef.current.videoWidth
                    canvas.height = videoRef.current.videoHeight
                  }
                  // Draw frame to canvas
                  ctx.drawImage(videoRef.current, 0, 0)
                  // Decode from canvas element using decodeFromImageElement
                  try {
                    // Convert canvas to image data URL and decode
                    const result = await reader.decodeFromImageUrl(canvas.toDataURL('image/jpeg', 0.8))
                    if (result) {
                      const barcode = result.getText()
                      console.log('[BarcodeScanner] Barcode detected after', scanCount, 'attempts:', barcode)
                      playBeep()
                      showFlashEffect()
                      setDetectedBarcode(barcode)
                      setState('detected')
                      stopScanner()
                      return
                    }
                  } catch (decodeErr) {
                    // NotFoundException is expected when no barcode is visible
                    if (!(decodeErr instanceof NotFoundException)) {
                      console.debug('[BarcodeScanner] Decode error:', decodeErr)
                    }
                  }
                }
              }
            } catch (err) {
              console.debug('[BarcodeScanner] Loop error:', err)
            }
            scanCount++
            // Short delay between scans (100ms = ~10 scans/second)
            await new Promise(resolve => setTimeout(resolve, 100))
          }
        }
        decodeLoop()

      } catch (error) {
        console.error('[BarcodeScanner] Initialization error:', error)
        setState('error')

        if (error instanceof Error) {
          const errorName = (error as DOMException).name || error.name
          if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
            setErrorType('permission_denied')
          } else if (errorName === 'NotFoundError' || errorName === 'NotReadableError') {
            setErrorType('no_camera')
          } else {
            setErrorType('unknown')
          }
        } else {
          setErrorType('unknown')
        }
      }
    }

    initScanner()

    return () => {
      stopScanner()
    }
  }, [playBeep, showFlashEffect, stopScanner, scanKey])

  // Handle confirm button
  const handleConfirm = () => {
    if (detectedBarcode) {
      onScan(detectedBarcode)
      onClose()
    }
  }

  // Handle retry (scan again)
  const handleRetry = () => {
    stopScanner()
    setDetectedBarcode(null)
    setAiConfidence(null)
    setAiError(null)
    setShowAIButton(false)
    setState('initializing')
    setScanKey((k) => k + 1)
  }

  // Render error content
  const renderError = () => {
    let title = ''
    let message = ''
    const icon = <AlertCircle className="text-red-500" size={48} />
    let showPermissionHelp = false

    switch (errorType) {
      case 'no_camera':
        title = 'No Camera Found'
        message = 'We couldn\'t find a camera on your device. Make sure your camera is connected and try again.'
        break
      case 'permission_denied':
        title = 'Camera Access Denied'
        message = 'Come on, we promise we\'re not taking selfies! We\'ll only use it to read those boring black-and-white stripes.'
        showPermissionHelp = true
        break
      default:
        title = 'Something Went Wrong'
        message = 'An unexpected error occurred while accessing the camera. Please try again or enter the barcode manually.'
    }

    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        {icon}
        <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-sm">{message}</p>

        {showPermissionHelp && (
          <div className="mt-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg text-left w-full max-w-sm">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
              <Settings size={16} />
              How to enable camera access:
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">Browser settings:</p>
            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 ml-6 list-decimal mb-3">
              <li>Click the <span className="inline-flex items-center gap-0.5 font-medium">lock icon <ExternalLink size={10} /></span> in your browser&apos;s address bar</li>
              <li>Find <span className="font-medium">&quot;Camera&quot;</span> and set to <span className="font-medium">&quot;Allow&quot;</span></li>
            </ol>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">Windows 10/11 users:</p>
            <ol className="text-xs text-gray-600 dark:text-gray-400 space-y-1 ml-6 list-decimal">
              <li>Open <span className="font-medium">Settings → Privacy → Camera</span></li>
              <li>Enable <span className="font-medium">&quot;Allow desktop apps to access your camera&quot;</span></li>
            </ol>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          {showPermissionHelp && (
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          )}
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors ${
              showPermissionHelp
                ? 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                : 'bg-primary-600 text-white hover:bg-primary-700'
            }`}
          >
            {showPermissionHelp ? 'Cancel' : 'Got it'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      {/* Hidden canvas for frame capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
      >
        <X size={24} />
      </button>

      {/* Main content */}
      <div className="relative w-full max-w-lg mx-4">
        {state === 'error' ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
            {renderError()}
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b dark:border-gray-700 flex items-center gap-3">
              <Camera className="text-primary-600" size={24} />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {state === 'detected' ? 'Barcode Detected!' : state === 'analyzing' ? 'Analyzing...' : 'Scan Barcode'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {state === 'detected'
                    ? 'Confirm to use this barcode'
                    : state === 'analyzing'
                    ? 'AI is reading the barcode...'
                    : 'Position the barcode within the frame'}
                </p>
              </div>
            </div>

            {/* Video container */}
            <div className="relative aspect-[4/3] bg-black">
              {/* Video element */}
              <video
                ref={videoRef}
                className={`w-full h-full object-cover ${state === 'detected' || state === 'analyzing' ? 'opacity-50' : ''}`}
                playsInline
                muted
                autoPlay
              />

              {/* Scanner overlay */}
              {(state === 'scanning' || state === 'analyzing') && (
                <div className="absolute inset-0 pointer-events-none">
                  {/* Semi-transparent overlay */}
                  <div className="absolute inset-0 bg-black/40" />

                  {/* Scanning area cutout */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[280px] h-[140px]">
                    {/* Clear area */}
                    <div className="absolute inset-0 bg-black/0" style={{
                      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)'
                    }} />

                    {/* Corner brackets */}
                    <div className="absolute inset-0">
                      {/* Top-left */}
                      <div className="absolute top-0 left-0 w-8 h-8 border-l-4 border-t-4 border-primary-500" />
                      {/* Top-right */}
                      <div className="absolute top-0 right-0 w-8 h-8 border-r-4 border-t-4 border-primary-500" />
                      {/* Bottom-left */}
                      <div className="absolute bottom-0 left-0 w-8 h-8 border-l-4 border-b-4 border-primary-500" />
                      {/* Bottom-right */}
                      <div className="absolute bottom-0 right-0 w-8 h-8 border-r-4 border-b-4 border-primary-500" />
                    </div>

                    {/* Animated scan line */}
                    {state === 'scanning' && (
                      <div className="absolute left-2 right-2 h-0.5 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-scan-line" />
                    )}
                  </div>
                </div>
              )}

              {/* Green flash effect */}
              {showFlash && (
                <div className="absolute inset-0 bg-green-500/30 animate-pulse" />
              )}

              {/* Loading state */}
              {state === 'initializing' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="text-center text-white">
                    <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
                    <p className="mt-4 text-sm">Initializing camera...</p>
                  </div>
                </div>
              )}

              {/* Analyzing state */}
              {state === 'analyzing' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg text-center">
                    <Loader2 className="mx-auto text-primary-500 mb-2 animate-spin" size={32} />
                    <p className="text-sm text-gray-600 dark:text-gray-400">AI is analyzing the image...</p>
                  </div>
                </div>
              )}

              {/* Detected barcode display */}
              {state === 'detected' && detectedBarcode && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-lg text-center">
                    <Check className="mx-auto text-green-500 mb-2" size={32} />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {aiConfidence ? `AI detected (${aiConfidence} confidence):` : 'Detected barcode:'}
                    </p>
                    <p className="text-2xl font-mono font-bold text-gray-900 dark:text-white mt-1">
                      {detectedBarcode}
                    </p>
                    {aiConfidence && aiConfidence !== 'high' && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                        Please verify the barcode is correct
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer with actions */}
            {state === 'detected' && (
              <div className="p-4 border-t dark:border-gray-700 flex gap-3">
                <button
                  onClick={handleRetry}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <RefreshCw size={18} />
                  Scan Again
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Check size={18} />
                  Confirm
                </button>
              </div>
            )}

            {/* Scanning footer with AI button */}
            {state === 'scanning' && (
              <div className="p-4 border-t dark:border-gray-700">
                {aiError && (
                  <p className="text-sm text-red-500 dark:text-red-400 mb-3 text-center">
                    {aiError}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Hold the barcode steady within the frame
                  </p>
                  {showAIButton && (
                    <button
                      onClick={handleAICapture}
                      className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm font-medium shadow-md"
                      title="Use AI to read the barcode from the current frame"
                    >
                      <Sparkles size={16} />
                      AI Read
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Analyzing footer */}
            {state === 'analyzing' && (
              <div className="p-4 border-t dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Please wait while AI analyzes the barcode...
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSS for scan line animation */}
      <style>{`
        @keyframes scan-line {
          0%, 100% {
            top: 10%;
          }
          50% {
            top: 90%;
          }
        }
        .animate-scan-line {
          animation: scan-line 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}
